import { useEffect } from "react";
import { ShieldCheck, Target, Zap, Scale, Eye } from "lucide-react";
import SpotlightCard from "@/components/SpotlightCard";
import CTABanner from "@/components/CTABanner";
import { Reveal, Eyebrow, MaskLines } from "@/components/Reveal";

const IMG_INSPECTOR = "https://images.unsplash.com/photo-1507497806295-753c4108560c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwaW5zcGVjdG9yJTIwcGlwZWxpbmUlMjByZWZpbmVyeSUyMHdlbGRpbmclMjBoaWdoJTIwcXVhbGl0eXxlbnwwfHx8fDE3ODk1MDU0MjV8MA&ixlib=rb-4.1.0&q=85";

const chapters = [
  { num: "01", title: "Dependable Field Expertise", body: "APX was built in the field, not the boardroom. Our people have stood the watch on plant floors, pipeline spreads, and turnaround scaffolds — and it shows in every judgment call we make." },
  { num: "02", title: "Technical Accuracy", body: "A finding is only as good as the rigor behind it. We hold every interpretation, measurement, and report to the governing code, verified by ASNT Level III review." },
  { num: "03", title: "Responsive Project Execution", body: "Schedules do not bend for quality — quality must move at schedule speed. APX mobilizes fast, communicates clearly, and never becomes the critical path." },
  { num: "04", title: "Integrity & Accountability", body: "We sign our names to our work and stand behind it. Independent findings, honest reporting, and full traceability — even when the answer is not the easy one." },
];

const values = [
  { icon: ShieldCheck, title: "Safety", desc: "No inspection result is worth an injury. Safety leads every engagement." },
  { icon: Target, title: "Quality", desc: "The standard is the standard — on every weld, report, and record." },
  { icon: Scale, title: "Integrity", desc: "Objective, independent findings our clients can defend anywhere." },
  { icon: Zap, title: "Accountability", desc: "We own our calls, our timelines, and our documentation." },
  { icon: Eye, title: "Attention to Detail", desc: "Defects hide in details. So does excellence. We live in both." },
];

export default function About() {
  useEffect(() => {
    document.title = "About | APX Project Solutions LLC";
  }, []);

  return (
    <div data-testid="about-page">
      <section className="tech-grid relative overflow-hidden border-b border-white/[0.08]" data-testid="about-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-24 sm:px-8 sm:py-32 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <Eyebrow>About APX Project Solutions LLC</Eyebrow>
            <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.92] tracking-tight text-white sm:text-7xl">
              <MaskLines lines={[<>Field truth,</>, <span className="text-apx">documented.</span>]} />
            </h1>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">
              APX Project Solutions LLC exists for one reason: to give owners and project teams an honest, expert, Level III–led view of their inspection and quality reality — and the documentation to prove it.
            </p>
          </div>
          <Reveal delay={0.2}>
            <div className="chamfer relative overflow-hidden border border-white/10">
              <img src={IMG_INSPECTOR} alt="APX field inspector" className="h-[380px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 px-5 py-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">ASNT Level III Leadership</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="about-manifesto">
        <Reveal>
          <Eyebrow>The APX Manifesto</Eyebrow>
          <h2 className="mt-6 max-w-2xl font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-6xl">
            Four chapters. One promise.
          </h2>
        </Reveal>
        <div className="mt-16 space-y-0">
          {chapters.map((c, i) => (
            <Reveal key={c.num} delay={0.05}>
              <div className="grid gap-6 border-t border-white/[0.08] py-12 last:border-b lg:grid-cols-[180px_1fr_1.4fr] lg:items-start" data-testid={`manifesto-chapter-${c.num}`}>
                <span className="font-display text-7xl font-black leading-none text-outline-orange sm:text-8xl">{c.num}</span>
                <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-white sm:text-3xl lg:pt-3">{c.title}</h3>
                <p className="text-sm leading-relaxed text-zinc-400 sm:text-base lg:pt-4">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.08] bg-coal py-24 sm:py-32" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Core Values</Eyebrow>
            <h2 className="mt-6 max-w-2xl font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-6xl">
              What we refuse to compromise
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden border border-white/[0.08] bg-white/[0.08] sm:grid-cols-2 lg:grid-cols-5">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07}>
                <SpotlightCard className="h-full bg-coal p-7 transition-colors hover:bg-surface" data-testid={`value-card-${i}`}>
                  <v.icon className="h-6 w-6 text-apx" strokeWidth={1.5} />
                  <h3 className="mt-6 font-display text-lg font-bold uppercase tracking-wide text-white">{v.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-zinc-400">{v.desc}</p>
                </SpotlightCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
